import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { AsyncConnector, useAsyncConnectorList } from '@axiom/ui';

import { LegacyLawSchoolsApi } from '../../api/legacy-law-schools-api';
import type { ReadLawSchoolsResponse } from '../../api/generated/dtos/read-law-schools-response';
import type { ReadLawSchoolsResponseEntity } from '../../api/generated/entities/read-law-schools-response-entity';

export type UseAdminModalLawSchoolsData = {
  results: ReadLawSchoolsResponseEntity[];
  hasMore: boolean;
  loadMore: () => void;
  onSearchChange: (value: string) => void;
  setRankFilter: (rank: string | undefined) => void;
};

// The list rows are read FROM the Data Client store: each page the connector
// fetches normalizes in, and `useAsyncConnectorList` re-derives the rows on every
// store commit. A save is just a normal `Api.write` (PATCH/PUT/POST) that merges
// into the same `LawSchool` records, so it shows up on the next commit with no
// refetch — no mutation handling lives here. This side-steps the Elasticsearch
// read lag that made a re-read after save return the pre-save row.
export const useAdminModalLawSchoolsData = (): UseAdminModalLawSchoolsData => {
  const [searchTerm, setSearchTerm] = useState('');
  const [rankFilter, setRankFilter] = useState<string | undefined>();

  const connector = useMemo(
    () =>
      new AsyncConnector<ReadLawSchoolsResponse, ReadLawSchoolsResponseEntity>({
        getData: ({ search, page }) =>
          LegacyLawSchoolsApi.readLawSchools({
            query: {
              search: search || undefined,
              page,
              filters: rankFilter ? { tags: [rankFilter] } : undefined,
            },
          }),
        // Generated search DTO is loosely typed; rows are entity-shaped at runtime.
        parseData: response => ({
          currentPage: response.meta?.currentPage ?? 1,
          totalPages: response.meta?.pageCount ?? 0,
          data: response.data,
        }),
      }),
    [rankFilter]
  );

  const { items, hasMore } = useAsyncConnectorList(connector);

  // Fetch page 1 on mount and whenever the search term or filter (new connector)
  // changes.
  useEffect(() => {
    void connector.reload({ search: searchTerm });
  }, [connector, searchTerm]);

  // Run the connector's teardown only when it is swapped out or unmounts.
  useEffect(
    () => () => {
      connector.cleanup();
    },
    [connector]
  );

  const onSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 250),
    []
  );

  // Apply the active rank filter client-side over the live store rows. The
  // server-side filter only fixes membership at fetch time; re-applying it to
  // the store data means a saved rank change that no longer matches drops the
  // row immediately — without re-reading the lagged search index.
  const results = useMemo(
    () =>
      rankFilter
        ? items.filter(school => school.tags.some(tag => tag.id === rankFilter))
        : items,
    [items, rankFilter]
  );

  return {
    results,
    hasMore,
    loadMore: () => {
      void connector.loadMore();
    },
    onSearchChange,
    setRankFilter,
  };
};
